/**
 * Service pages.
 *
 * `slug` is the exact path the old WordPress site was indexed under, so these
 * pages keep their URLs rather than redirecting somewhere else.
 */
export interface ServiceSection {
  heading: string;
  body: string;
}

export interface ServiceContent {
  slug: string;
  /** Short label for cards and navigation. */
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  tagline: string;
  intro: string;
  points: string[];
  sections: ServiceSection[];
  icon: string;
  featured?: boolean;
}

const ICONS = {
  car: 'M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
  foam: 'M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z',
  wheel:
    'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-6 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm-5.9-1.4L3.4 9.3m11.5 1.3 5.7-1.3M12 15v6',
  sparkle:
    'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.091 3.091ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z',
  swatch:
    'm4.098 19.902 a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z',
  droplet: 'M12 21a9 9 0 0 0 9-9c0-3.6-3-7.8-9-12C6 4.2 3 8.4 3 12a9 9 0 0 0 9 9Z',
  steam:
    'M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z',
  shield:
    'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
};

export const SERVICES: ServiceContent[] = [
  {
    slug: 'car-detailing',
    title: 'Car Detailing',
    h1: 'Car Detailing',
    metaTitle: 'Car Detailing Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Full car detailing in Hamilton, inside and out. Hand wash, paint correction, ceramic coating, leather and upholstery care. We come to you.',
    tagline: 'A comprehensive clean and restoration, inside and out.',
    intro:
      'Detailing is a comprehensive cleaning and restoration process that aims to bring your car back to its original condition, or even better. Every surface is worked through in turn, so the result is not just a clean car but a properly restored one.',
    featured: true,
    points: [
      'Hand wash and dry',
      'Waxing and polishing',
      'Paint correction',
      'Ceramic coating',
      'Headlight restoration',
      'Wheel and tyre cleaning',
      'Scratch removal and buffing',
      'Chrome polishing',
      'Leather cleaning and conditioning',
      'Carpet and upholstery shampoo',
      'Engine bay detailing',
      'Trim restoration',
    ],
    sections: [
      {
        heading: 'Exterior work',
        body: 'Hand wash and dry, waxing and polishing, paint correction, ceramic coating, headlight restoration, paint protection film, wheel and tyre cleaning, scratch removal, buffing and chrome polishing.',
      },
      {
        heading: 'Interior work',
        body: 'Leather cleaning and conditioning, carpet and upholstery shampooing, stain removal, pet hair removal, odour elimination, and mould and mildew removal. Engine bay detailing and trim restoration are included too.',
      },
      {
        heading: 'Why it is worth doing',
        body: 'We use the latest tools and equipment and only the best products and materials. A proper detail does not just make your car look great — it can improve its resale value and prolong its life.',
      },
    ],
    icon: ICONS.car,
  },
  {
    slug: 'exterior-car-wash',
    title: 'Exterior Car Wash',
    h1: 'Exterior Car Wash',
    metaTitle: 'Exterior Car Wash Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Professional exterior car washing in Hamilton. Hand wash, foam wash, waxing, wheels and tyres, headlight restoration. Mobile service to your door.',
    tagline: 'A thorough hand wash with a protective finish.',
    intro:
      'Dirt, grime and road dust lifted without marring your paint, then finished with protection that lasts well beyond the day we visit. Our mobile service means it happens at your home, office or wherever suits.',
    points: [
      'Hand wash and foam wash',
      'Wheel and tyre cleaning',
      'Waxing and polishing',
      'Bug and tar removal',
      'Headlight restoration',
      'Window cleaning',
      'Engine bay cleaning',
      'Buffing',
    ],
    sections: [
      {
        heading: 'What it protects against',
        body: 'Road grime, dust and environmental fallout do not just look bad — left on the paint they etch into the clear coat. Washing regularly and sealing afterwards keeps the finish intact and the vehicle worth more.',
      },
      {
        heading: 'Flexible and mobile',
        body: 'We bring our own water and power, so you do not need to provide anything. Book us at home, at the office, or anywhere else convenient.',
      },
    ],
    icon: ICONS.foam,
  },
  {
    slug: 'interior-car-wash',
    title: 'Interior Car Wash',
    h1: 'Interior Car Wash',
    metaTitle: 'Interior Car Wash Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Interior car cleaning in Hamilton. Vacuum, carpet shampoo, upholstery, leather conditioning, odour and pet hair removal. Mobile service.',
    tagline: 'A full interior clean, vacuum and polish.',
    intro:
      'Crumbs, pet hair, fingerprints and musty odours dealt with properly. Meticulous cleaning reaches the hidden nooks in the car, while detailing revitalises the upholstery and surfaces.',
    points: [
      'Full vacuum including the boot',
      'Carpet shampooing',
      'Upholstery cleaning',
      'Leather conditioning',
      'Dashboard and door panels',
      'Interior windows and headliner',
      'Air vent and seat belt cleaning',
      'Pet hair and odour removal',
    ],
    sections: [
      {
        heading: 'More than a tidy up',
        body: 'Stain removal, mould and mildew removal, seat belt cleaning and air vent cleaning are all part of the job. The aim is a car that not only looks and smells clean but has noticeably better air quality inside.',
      },
      {
        heading: 'Every surface, not just the obvious ones',
        body: 'Boot, glove box, centre console, cup holders and door jambs are easy to skip and the first places grime builds up. We work through them all.',
      },
    ],
    icon: ICONS.wheel,
  },
  {
    slug: 'waxing-polishing',
    title: 'Waxing & Polishing',
    h1: 'Waxing & Polishing',
    metaTitle: 'Car Waxing & Polishing Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Car waxing and polishing in Hamilton. Swirl mark removal, clear coat protection, scratch and oxidation removal, chrome and headlight polishing.',
    tagline: 'A two step treatment that restores depth and adds protection.',
    intro:
      'Think of it as a two-step spa treatment for your car. Polishing removes the imperfections; wax lays down protective layers on top. Together they restore depth of colour and leave a glass-smooth surface that is far easier to keep clean.',
    points: [
      'Hand and machine waxing',
      'Paint polishing and restoration',
      'Swirl mark removal',
      'Clear coat protection',
      'Scratch and oxidation removal',
      'Headlight and chrome polishing',
      'Tar and bug removal',
      'Wheel and exhaust tip polishing',
    ],
    sections: [
      {
        heading: 'Polish then protect',
        body: 'Polishing is corrective: it takes out swirls, light scratches and oxidation. Wax is protective: it shields what the polish just restored from UV, rain and road contamination.',
      },
      {
        heading: 'Beyond the paint',
        body: 'Tar and bug removal, paint touch-ups, wheel polishing and exhaust tip restoration are all part of the service, so the whole car lifts rather than just the panels.',
      },
      {
        heading: 'Worth the investment',
        body: 'The result does not just improve the appearance of your car, it helps protect it from the elements, prolonging its lifespan and maintaining its value.',
      },
    ],
    icon: ICONS.sparkle,
  },
  {
    slug: 'paint-correction',
    title: 'Paint Correction',
    h1: 'Paint Correction',
    metaTitle: 'Paint Correction Hamilton | Cut & Polish | Fine Shine Car Wash',
    metaDescription:
      'Machine paint correction in Hamilton. Stage 1 and 2 cut and polish removes swirls, scratches, water spots and oxidation for a showroom finish.',
    tagline: 'Multi stage correction for a deep, even gloss.',
    intro:
      'Machine polishing that removes swirls, oxidation and light scratches to bring back a deep, even gloss. Available as a single stage refresh or a full multi stage cut and polish, depending on how far the paint has gone.',
    points: [
      'Machine polish',
      'Cut and polish, stage 1 and 2',
      'Clay bar decontamination',
      'Swirl and scratch removal',
      'Water spot and oxidation removal',
      'Finished with wax or sealant',
    ],
    sections: [
      {
        heading: 'What it fixes',
        body: 'Scratch removal, oxidation removal, water spot removal and a marked improvement in clarity. Our detailers remove scratches, swirls and oxidation, leaving a flawless, showroom-quality finish.',
      },
      {
        heading: 'What it costs',
        body: 'Price depends on the severity of the damage, the size of the vehicle and how many stages are needed. We quote after inspecting the paint rather than guessing up front — machine polish starts from $260 and full correction from $300.',
      },
      {
        heading: 'Dark paint needs more care',
        body: 'Black and dark colours show every imperfection, so they need specific techniques and a more careful finishing stage to avoid leaving haze behind.',
      },
    ],
    icon: ICONS.swatch,
  },
  {
    slug: 'shampoo-leather-treatment',
    title: 'Shampoo & Leather Treatment',
    h1: 'Shampoo & Leather Treatment',
    metaTitle: 'Car Shampoo & Leather Treatment Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Interior shampoo and leather treatment in Hamilton. Seats, carpets and mats shampooed, leather cleaned and conditioned, odours and stains removed.',
    tagline: 'Deep interior cleaning with proper leather care.',
    intro:
      'Two complementary treatments. Shampooing removes grime from carpets, upholstery and mats, making them look fresher and well groomed. Leather treatment nourishes and protects, preventing cracking, fading and other ageing problems.',
    points: [
      'Interior shampooing',
      'Upholstery, carpet and floor mats',
      'Seat and leather cleaning',
      'Leather conditioning',
      'Boot, glove box and console',
      'Dashboard, door panels and headliner',
      'Stain and odour elimination',
      'Pet hair, mould and mildew removal',
    ],
    sections: [
      {
        heading: 'Everywhere the dirt hides',
        body: 'Steering wheel and seat belts, cup holders, air vents and the ashtray all get attention, along with the obvious surfaces. Trained technicians use current techniques and equipment, and the service works on all vehicle types.',
      },
      {
        heading: 'Why keep on top of it',
        body: 'Left alone, dirt and grime grind into fabric and leather and cause premature wear. Regular treatment is what keeps an interior looking near-new years in.',
      },
    ],
    icon: ICONS.droplet,
  },
  {
    slug: 'full-interior-steam-clean',
    title: 'Full Interior Steam Clean',
    h1: 'Full Interior Steam Clean',
    metaTitle: 'Full Interior Steam Clean Hamilton | Fine Shine Car Wash',
    metaDescription:
      'Interior steam cleaning in Hamilton. Over 200 degree vapour disinfects, kills bacteria, removes dust mites and odours, and dries fast.',
    tagline: 'Chemical-free deep clean that disinfects and deodorises.',
    intro:
      'High pressured air and warm vapour used as a tool to disinfect and eliminate odour from your car interior. At temperatures above 200 degrees the steam penetrates deep into fabric and carpet where a vacuum simply cannot reach.',
    points: [
      'Deep dirt and dust extraction',
      'Tough stain treatment',
      'Kills bacteria and germs',
      'Removes dust mites and allergens',
      'Smoke, pet and mildew odour removal',
      'Improves cabin air quality',
      'No harsh chemicals',
      'Minimal drying time',
    ],
    sections: [
      {
        heading: 'Dirt, stains and odour',
        body: 'Steam shakes out and removes dirt, skin mites and heavy dust from between the carpets and upholstery. It lifts stubborn marks like coffee, food and pet accidents, and puts away residual odour from smoke, pets and mildew.',
      },
      {
        heading: 'Healthier to sit in',
        body: 'The process kills bacteria and germs and detaches dust mites, pet dander and allergens, which makes a real difference to air quality and to anyone in the car who reacts to them.',
      },
      {
        heading: 'Back on the road sooner',
        body: 'Steam needs far less drying time than shampooing, so the car is usable again quickly. It also achieves all of this without harsh chemicals.',
      },
    ],
    icon: ICONS.steam,
  },
  {
    slug: 'ceramic-coating',
    title: 'Ceramic Coating & Paint Protection',
    h1: 'Ceramic Coating & Paint Protection',
    metaTitle: 'Ceramic Coating Hamilton | Paint Protection | Fine Shine Car Wash',
    metaDescription:
      'Ceramic coating and paint protection in Hamilton. A liquid polymer that bonds to your paint for lasting gloss and far easier cleaning.',
    tagline: 'Long lasting protection that keeps paint looking newer.',
    intro:
      'Ceramic coating is a liquid polymer that bonds to the paint and forms a protective layer over it. It outlasts traditional wax by a wide margin, holds a deeper gloss, and makes the car noticeably easier to wash.',
    points: [
      'Ceramic spray coating',
      'Full body protective wax',
      'Paint sealant',
      'Plastic and bumper trim treatment',
      'Headlight restoration',
      'Paint protection film application',
    ],
    sections: [
      {
        heading: 'Shine and protection together',
        body: 'The coating adds depth and gloss while shielding the paint underneath from the sun and from unpredictable weather. Dirt struggles to bond to the surface, so washes are quicker and gentler.',
      },
      {
        heading: 'Correct first, then coat',
        body: 'A coating locks in whatever is underneath it, so any swirls or scratches should be corrected before it goes on. We usually pair coating with paint correction for that reason.',
      },
      {
        heading: 'Applied properly',
        body: 'Preparation and application technique are what separate a coating that lasts from one that fails early. It is worth having done by someone who does it regularly rather than from a kit.',
      },
    ],
    icon: ICONS.shield,
  },
];

export const SERVICE_BY_SLUG = new Map(SERVICES.map((service) => [service.slug, service]));
